<!--
  The reference chart: every vocabulary class, with the OCIANA font's letter form where the
  repo has one.

  Two honesty points are built into the markup rather than left to a caption:

  * **A letter with no form gets an explicit gap, never a borrowed glyph.** The cell is a
    labelled "no reference form" tile. All 28 attested letters now have a form, so in practice
    this branch renders only for `illegible` and `unknown` — but it is the reason the chart can
    be trusted, and it is driven by `entry.glyph === null` rather than by a letter list.
    Where a form came from is shown per cell: the four letters absent from every cached page
    (`ḏ`, `ġ`, `k`, `ẓ` — `k` alone is 2% of the corpus) come from Internet Archive snapshots
    of the original OCIANA site. Same renderer, byte-verified, but a different provenance, and
    a chart that hid the difference would be asking to be trusted on that point rather than
    showing its working.
  * **A sound value says how firmly it is held, or admits there is none.** The script is
    consonantal, so a value here is a consonant and never a vowel. `†` marks a value with
    rival readings — the same role `⧉` plays for a form's provenance, a mark plus a title
    rather than colour, so it survives greyscale and a screen reader. Nothing in the table
    has been reviewed by an epigrapher yet and the caption says so; a letter with no value
    gets an explicit "no value", never a borrowed one.
  * **Mirroring is a toggle, and it says which way is which.** The chart draws the stored bytes;
    OCIANA's own page renders that row flipped horizontally in CSS, so the two are each other's
    mirror. Which of them a right-to-left text carries is **not settled here** and is filed as
    F1 in docs/annotation_guidelines.md §12 — so the chart claims neither, lets you flip the
    whole chart or a single cell, and labels the state it is in.
-->
<script lang="ts">
  import { ALPHABET, type AlphabetEntry } from '../lib/alphabet.generated';
  import { PHONOLOGY_SOURCE, hasSoundValue, isContested, soundValue } from '../lib/phonology';
  import { clipCaveat, hasClip, playLetter } from '../lib/audio';
  import { citationsFor } from '../lib/phonology';
  import InfoTip from './InfoTip.svelte';

  interface Props {
    entries?: readonly AlphabetEntry[];
  }

  const { entries = ALPHABET }: Props = $props();

  // `false` draws the artifact's stored bytes; `true` draws their mirror. Which of the two is
  // the form the stone carries is open — guidelines §12 F1 — so neither state is called correct
  // here. The global toggle flips the whole chart; a click on a single glyph flips just that
  // one, overriding the global setting.
  let mirrorAll = $state(false);
  let flipped = $state(new Set<number>());

  function mirrored(id: number): boolean {
    return flipped.has(id) ? !mirrorAll : mirrorAll;
  }

  function toggleCell(id: number): void {
    const next = new Set(flipped);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    flipped = next;
  }

  function frequencyLabel(entry: AlphabetEntry): string {
    if (entry.frequency === null) return '';
    return `${(entry.frequency * 100).toFixed(2)}%`;
  }

  // Colour is never the only signal here either: an archive-sourced form carries the ⧉ mark as
  // well as this tooltip, so the provenance survives a greyscale print and a screen reader.
  function sourceTitle(entry: AlphabetEntry): string {
    if (entry.sourceKind === 'archive') {
      return (
        `Form from ${entry.sourceSiglum}, recovered from an Internet Archive snapshot ` +
        `(${entry.sourceSnapshot}) of the original OCIANA page`
      );
    }
    return `Form from ${entry.sourceSiglum}, an OCIANA page cached in this repository`;
  }
</script>

<section class="panel col" aria-labelledby="chart-heading" data-section="chart">
  <div class="row wrap head">
    <h2 id="chart-heading">Letter forms</h2>
    <span class="spacer"></span>
    <label class="row small mirror-toggle">
      <input type="checkbox" bind:checked={mirrorAll} data-testid="mirror-all" />
      Mirror all
    </label>
  </div>
  <p class="small muted note">
    Each form is the OCIANA font's rendering of the letter, recovered from the record pages;
    the number is the letter's share of the corpus and the siglum below it is the page the form
    came from — <span class="mono">⧉</span> marks one read from an Internet Archive snapshot
    rather than a page cached here. What you see is the <strong>stored bytes</strong>; OCIANA's
    own page displays that same row flipped horizontally in CSS, so the two are each other's
    mirror. <strong>Which of the two a right-to-left text carries is not settled here</strong> —
    the question is filed as F1 in §12 of the guidelines, for an epigrapher. <strong>It does not
    affect boxes you draw</strong>: what a box records is named against this stored form, so it
    stays valid whichever way F1 resolves. Click a glyph to flip just that one.
  </p>
  <p class="small muted note">
    The bracketed value under each letter is its reconstructed <strong>consonant</strong> — this
    script records no vowels, so there are none to show.
    <span class="mono">†</span> marks a value with rival readings; hover it for the
    alternatives and the works cited. Of {PHONOLOGY_SOURCE.secureCount +
      PHONOLOGY_SOURCE.contestedCount} values recorded, {PHONOLOGY_SOURCE.contestedCount} are
    contested — the numbered sibilants <span class="glyph">s¹</span>/<span class="glyph"
      >s²</span
    >
    are written with numerals precisely because their phonetic values are not agreed.
    <strong>None of these values has been reviewed by an epigrapher</strong>; they are drafted
    from the published literature and are provisional.
  </p>
  <p class="small muted note">
    <span class="mono">▸</span> plays a synthesized clip. Each is the consonant between two
    <strong>support vowels</strong> — a bare stop is silent, so "aba" is how you demonstrate a
    <span class="glyph">b</span>. Those vowels are a way of making the consonant audible; they are
    not part of the letter and no vowel is recorded anywhere in this corpus. Four of the emphatics
    come out weaker than the value above says, and the play button's tooltip names which.
  </p>

  <ul class="grid" role="list">
    {#each entries as entry (entry.id)}
      <li class="cell col" class:special={entry.kind === 'special'} data-letter={entry.letter}>
        <div class="art">
          {#if entry.glyph}
            <button
              type="button"
              class="glyph-btn"
              class:mirror={mirrored(entry.id)}
              title={mirrored(entry.id) ? 'Showing flipped' : 'Showing the stored form'}
              aria-pressed={mirrored(entry.id)}
              onclick={() => toggleCell(entry.id)}
            >
              <img src={entry.glyph} alt={`Hismaic letter ${entry.display}`} width="30" height="40" />
            </button>
          {:else}
            <div class="no-form" title="No reference form in this repository">
              <span class="small muted">no form</span>
            </div>
          {/if}
        </div>
        <div class="label row">
          <span class="glyph translit">{entry.display}</span>
          <span class="mono muted id">{entry.id}</span>
        </div>
        {#if entry.kind !== 'special'}
          {#if hasSoundValue(entry.id)}
            <span class="row sound-row">
              <span class="small mono sound" class:contested={isContested(entry.id)}>
                [{soundValue(entry.id)?.ipa}]{isContested(entry.id) ? ' †' : ''}
              </span>
              <!--
                The rival readings and the synthesizer's divergence are the honesty-critical
                half of this cell, so they are a button toggling a role="note" rather than a
                native tooltip: a title= is unreachable from the keyboard and unreliable on
                touch (see InfoTip.svelte, and the rule in annotator-api-frontend.md).
              -->
              {#if isContested(entry.id) || soundValue(entry.id)?.render?.diverges}
                <InfoTip label={`the reading of ${entry.display}`}>
                  {#if isContested(entry.id)}
                    <p class="tip-line">
                      <strong>Contested.</strong> Also read as
                      {soundValue(entry.id)
                        ?.alternatives.map((alternative) => `[${alternative.ipa}]`)
                        .join(', ')}.
                    </p>
                    {#each soundValue(entry.id)?.alternatives ?? [] as alternative (alternative.ipa)}
                      {#if alternative.note}
                        <p class="tip-line">[{alternative.ipa}] — {alternative.note}</p>
                      {/if}
                    {/each}
                  {/if}
                  {#if soundValue(entry.id)?.render?.diverges}
                    <p class="tip-line">
                      <strong>The audio approximates this.</strong>
                      {soundValue(entry.id)?.render?.note}
                    </p>
                  {/if}
                  {#if soundValue(entry.id)?.note}
                    <p class="tip-line">{soundValue(entry.id)?.note}</p>
                  {/if}
                  <p class="tip-line muted">
                    {#each citationsFor(entry.id) as reference, index (reference.key)}
                      {index > 0 ? '; ' : ''}{reference.author}{reference.year
                        ? ` ${reference.year}`
                        : ''}, {reference.title}
                    {/each}
                  </p>
                  <p class="tip-line muted">Not yet reviewed by an epigrapher.</p>
                </InfoTip>
              {/if}
              <!-- No control at all when there is no clip, rather than a dead one that
                   does nothing when pressed. Same rule as the missing-form tile. -->
              {#if hasClip(entry.id)}
                <button
                  type="button"
                  class="play"
                  aria-label={`Hear ${entry.display}`}
                  title={clipCaveat(
                    entry.id,
                    soundValue(entry.id)?.render?.produces ?? null,
                    soundValue(entry.id)?.ipa ?? null,
                  ) ?? undefined}
                  onclick={() => void playLetter(entry.id)}
                >
                  ▸
                </button>
              {/if}
            </span>
          {:else}
            <span class="small muted no-value" title="No sound value recorded in this repository">
              no value
            </span>
          {/if}
        {/if}
        {#if entry.kind === 'special'}
          <span class="small muted freq">{entry.letter}</span>
        {:else}
          <span class="small muted freq" title={`${entry.corpusCount} instances in the corpus`}>
            {entry.corpusCount.toLocaleString()} · {frequencyLabel(entry)}
          </span>
          {#if entry.sourceSiglum}
            <span class="small muted src mono" title={sourceTitle(entry)}>
              {entry.sourceSiglum}{entry.sourceKind === 'archive' ? ' ⧉' : ''}
            </span>
          {/if}
        {/if}
      </li>
    {/each}
  </ul>
</section>

<style>
  .head {
    gap: 8px;
  }

  .note {
    margin: 0;
    max-width: 82ch;
  }

  .mirror-toggle {
    gap: 6px;
    color: var(--text-dim);
  }

  .grid {
    list-style: none;
    margin: 0;
    padding: 0;
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(96px, 1fr));
    gap: 8px;
  }

  .cell {
    align-items: center;
    gap: 3px;
    padding: 8px 6px;
    border: 1px solid var(--line);
    border-radius: 6px;
    background: var(--bg-panel);
    text-align: center;
  }

  .cell.special {
    border-style: dashed;
  }

  .art {
    height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .glyph-btn {
    padding: 2px;
    background: var(--bg-input);
    border: 1px solid var(--line);
    border-radius: 4px;
    line-height: 0;
  }

  .glyph-btn img {
    /* The glyphs are small font renderings; a light background keeps the dark strokes visible. */
    background: var(--glyph-paper);
    border-radius: 2px;
  }

  .glyph-btn.mirror img {
    transform: scaleX(-1);
  }

  .no-form {
    width: 34px;
    height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px dashed var(--line-strong);
    border-radius: 4px;
  }

  .label {
    gap: 5px;
    align-items: baseline;
  }

  .translit {
    font-size: 1.1rem;
  }

  .id {
    font-size: var(--text-xs);
  }

  .freq,
  .src {
    font-size: var(--text-xs);
  }

  .sound-row {
    gap: 4px;
    align-items: center;
  }

  .sound {
    font-size: var(--text-xs);
    color: var(--text-dim);
  }

  .play {
    padding: 0 4px;
    font-size: var(--text-xs);
    line-height: 1.4;
    background: var(--bg-input);
    border: 1px solid var(--line);
    border-radius: 4px;
    color: var(--text-dim);
  }

  .play:hover {
    color: var(--text);
  }

  /* A contested value is marked by the dagger in the text as well as this weight, so the
     signal survives greyscale, a screen reader and a colour-blind reader. */
  .sound.contested {
    color: var(--accent, var(--text));
    font-weight: 600;
  }

  .tip-line {
    margin: 0 0 5px;
  }

  .tip-line:last-child {
    margin-bottom: 0;
  }

  .no-value {
    font-size: var(--text-xs);
    border: 1px dashed var(--line-strong);
    border-radius: 4px;
    padding: 0 4px;
  }
</style>
